// Aesthetic: Two-document comparison - the slip on the left, the invoice it is
// going onto on the right, nothing between them competing for attention. Blue
// marks the pairing; emerald is reserved for the act of attaching.
import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Paperclip,
  Loader2,
  Clock,
  HelpCircle,
  CheckCircle2,
  ShieldCheck,
  Pencil,
  Undo2
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import ReviewPreview from './ReviewPreview';
import ArchiveSearch from './ArchiveSearch';
import { cn } from '../lib/utils';

const prodBase = process.env.REACT_APP_API_BASE;
const invoiceContentRoute = process.env.REACT_APP_INVOICE_CONTENT || '/api/invoicecontent';

const contentUrl = (id) =>
  id ? `${prodBase}${invoiceContentRoute}?id=${encodeURIComponent(id)}` : null;

const STATE_STYLE = {
  matched: {
    label: 'Matched',
    blurb: 'Check the two documents, then attach.',
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
    accent: 'border-l-blue-500',
    Icon: CheckCircle2
  },
  waiting: {
    label: 'Waiting on the invoice',
    blurb: 'The voucher exists but its invoice has not been filed yet. This will attach itself when it is.',
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
    accent: 'border-l-amber-500',
    Icon: Clock
  },
  unmatched: {
    label: 'Needs a match',
    blurb: 'No purchase-order number that maps to a voucher. Find the invoice yourself.',
    badge: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    accent: 'border-l-slate-400',
    Icon: HelpCircle
  },
  unresolved: {
    label: 'Not checked yet',
    blurb: 'This slip has not been looked up. Re-check to try again.',
    badge: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    accent: 'border-l-slate-400',
    Icon: HelpCircle
  },
  attached: {
    label: 'Attached',
    blurb: 'Filed onto the voucher.',
    badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
    accent: 'border-l-emerald-500',
    Icon: CheckCircle2
  }
};

function Field({ label, value, mono }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline gap-2">
      <span className="w-24 shrink-0 text-[10px] font-medium uppercase tracking-wide text-slate-400">
        {label}
      </span>
      <span className={cn('text-xs text-slate-800 dark:text-slate-200', mono && 'tabular-nums')}>
        {value}
      </span>
    </div>
  );
}

/**
 * One packing slip and the invoice it is going onto.
 *
 * Both documents are shown as live PDFs rather than filenames, because
 * attaching changes a document that is already archived - the clerk should be
 * looking at what they are adding to, not trusting a name.
 */
export function AttachCard({ slip, busy, onAttach, onRecheck, onPickCandidate, onSendToReview }) {
  const style = STATE_STYLE[slip.state] || STATE_STYLE.unresolved;
  const { Icon } = style;

  // Rotation and crop on the slip, carried into the append exactly as filing
  // carries them. Slips come off the same scanner as everything else.
  const [transform, setTransform] = useState({ rotation: 0, crops: {} });
  // An override only exists once the clerk picks something in the search panel.
  const [override, setOverride] = useState(null);
  const [searching, setSearching] = useState(false);

  const target = override || (slip.targetItemId ? { id: slip.targetItemId, name: slip.targetFileName } : null);
  const canAttach = Boolean(target) && slip.state !== 'attached';
  const isOverride = Boolean(override && override.id !== slip.targetItemId);

  const updateTransform = (next) =>
    setTransform((prev) => (typeof next === 'function' ? next(prev) : next));

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, height: 0, marginBottom: 0 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className={cn(
        'overflow-hidden rounded-lg border border-l-4 bg-white dark:bg-slate-900',
        'border-slate-200 dark:border-slate-800',
        style.accent
      )}
    >
      <div className="p-4">
        {/* What it is, and where it is going - across the top, so the pairing
            reads before any of the detail does. */}
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={cn('gap-1 border-0 text-[10px]', style.badge)}>
            <Icon className="h-3 w-3" />
            {style.label}
          </Badge>

          <span className="truncate text-xs font-medium text-slate-700 dark:text-slate-300">
            {slip.vendorName || slip.fileName}
          </span>

          {slip.customerPoNumber && (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] tabular-nums text-slate-600 dark:bg-slate-800 dark:text-slate-400">
              PO {slip.customerPoNumber}
            </span>
          )}

          {target && (
            <>
              <ArrowRight className="h-3.5 w-3.5 shrink-0 text-slate-400" />
              <span className="truncate text-xs font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                {target.name || `V${slip.voucherNumber}`}
              </span>
            </>
          )}

          {slip.corroborated && !isOverride && (
            <Badge className="gap-1 border-0 bg-emerald-100 text-[10px] text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
              <ShieldCheck className="h-3 w-3" />
              Invoice number matches the slip
            </Badge>
          )}

          {isOverride && (
            <Badge className="gap-1 border-0 bg-blue-100 text-[10px] text-blue-800 dark:bg-blue-950 dark:text-blue-300">
              <Pencil className="h-3 w-3" />
              Chosen by hand
            </Badge>
          )}
        </div>

        <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
          {slip.state === 'waiting' && slip.voucherNumber
            ? `Voucher V${slip.voucherNumber} is in TIMS, but its invoice has not been archived yet. This slip will attach itself the moment that invoice is approved - there is nothing to do here.`
            : slip.reason || style.blurb}
        </p>

        <div className="mt-3 grid gap-4 lg:grid-cols-2">
          {/* The slip */}
          <div className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between">
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                The packing slip
              </h4>
              <span className="text-[10px] text-slate-400">{slip.fileName}</span>
            </div>

            <ReviewPreview
              url={contentUrl(slip.driveItemId)}
              startPage={slip.startPage}
              endPage={slip.endPage}
              fileName={slip.fileName}
              onExpand={() => window.open(contentUrl(slip.driveItemId), '_blank', 'noopener')}
              rotation={transform.rotation}
              crops={transform.crops}
              onTransformChange={updateTransform}
            />

            <div className="space-y-1 rounded border border-slate-200 p-2 dark:border-slate-800">
              <Field label="PO" value={slip.customerPoNumber} mono />
              <Field label="Order no." value={slip.orderNumber} mono />
              <Field label="Slip no." value={slip.packingSlipNumber} mono />
              <Field label="Invoice no." value={slip.slipInvoiceNumber} mono />
              <Field label="Ship date" value={slip.shipDate} mono />
            </div>
          </div>

          {/* What it is going onto */}
          <div className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between">
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {target ? 'Attaching to this invoice' : 'No invoice chosen'}
              </h4>
              {slip.voucherNumber && (
                <span className="text-[10px] tabular-nums text-slate-400">
                  V{slip.voucherNumber}
                </span>
              )}
            </div>

            {target ? (
              <ReviewPreview
                url={contentUrl(target.id)}
                fileName={target.name}
                onExpand={() => window.open(contentUrl(target.id), '_blank', 'noopener')}
                // The archived invoice is evidence here, not something being
                // edited - the slip is the only document this card changes.
                readOnly
              />
            ) : (
              <div className="flex min-h-[300px] items-center justify-center rounded border border-dashed border-slate-300 p-6 text-center text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
                {slip.state === 'waiting'
                  ? 'The invoice for this voucher is not in the archive yet.'
                  : 'Search below to find the invoice this slip belongs to.'}
              </div>
            )}

            <div className="space-y-1 rounded border border-slate-200 p-2 dark:border-slate-800">
              <Field label="TIMS invoice" value={slip.timsInvoiceNumber} mono />
              <Field label="TIMS date" value={slip.timsInvoiceDate} mono />
              <Field label="Filed as" value={target?.name} />
            </div>

            {/* A PO covering several invoices is a quarter of all matches. The
                candidates are the only defensible answers, so they are offered
                directly - searching for them again would be busywork. */}
            {slip.candidateVouchers?.length > 0 && slip.state !== 'attached' && (
              <div className="rounded border border-amber-200 bg-amber-50 p-2 dark:border-amber-900 dark:bg-amber-950/40">
                <p className="mb-1.5 text-[11px] font-medium text-amber-900 dark:text-amber-300">
                  This order was invoiced {slip.candidateVouchers.length} times. Which one is this slip for?
                </p>
                <div className="flex flex-col gap-1">
                  {slip.candidateVouchers.map((c) => (
                    <button
                      key={c.voucherNumber}
                      type="button"
                      onClick={() => onPickCandidate?.(slip, c)}
                      className="flex items-baseline gap-2 rounded border border-amber-300 bg-white px-2 py-1 text-left text-[11px] transition-colors hover:bg-amber-100 dark:border-amber-800 dark:bg-slate-900 dark:hover:bg-slate-800"
                    >
                      <span className="font-semibold tabular-nums">V{c.voucherNumber}</span>
                      <span className="tabular-nums text-slate-600 dark:text-slate-400">{c.invoiceNumber}</span>
                      <span className="ml-auto tabular-nums text-slate-400">{c.invoiceDate}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {slip.state !== 'attached' && (
              <div className="rounded border border-slate-200 p-2 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setSearching((s) => !s)}
                  className="mb-1 text-[11px] font-medium text-slate-600 underline-offset-2 hover:underline dark:text-slate-400"
                >
                  {searching ? 'Hide archive search' : target ? 'Attach to a different invoice' : 'Search the archive'}
                </button>
                {searching && (
                  <ArchiveSearch
                    initialQuery={slip.vendorName || ''}
                    selectedId={target?.id}
                    onSelect={(file) => setOverride({ id: file.id, name: file.name })}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            className="h-8 bg-emerald-600 text-xs hover:bg-emerald-700"
            disabled={!canAttach || busy}
            onClick={() => onAttach?.(slip, target, transform)}
          >
            {busy ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Paperclip className="mr-1 h-3.5 w-3.5" />
            )}
            {busy ? 'Attaching...' : 'Attach to this invoice'}
          </Button>

          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            disabled={busy}
            onClick={() => onRecheck?.(slip)}
          >
            Re-check
          </Button>

          {/* The way back. Sending a document here is one click, so undoing it
              has to be one click too - and nothing was destroyed on the way in,
              so this restores exactly what the reader originally proposed. */}
          {slip.state !== 'attached' && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 text-xs text-slate-500 transition-colors hover:bg-yellow-400 hover:text-black dark:text-slate-400 dark:hover:bg-yellow-500 dark:hover:text-black"
              disabled={busy}
              onClick={() => onSendToReview?.(slip)}
              title="This is an invoice, not a packing slip. Send it back to the Review Queue to be indexed."
            >
              <Undo2 className="mr-1 h-3.5 w-3.5" />
              Not a packing slip
            </Button>
          )}

          {!canAttach && slip.state === 'waiting' && (
            <span className="text-[11px] text-slate-500 dark:text-slate-400">
              Nothing to do - it is holding its place.
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default AttachCard;
